import React, { useState, useEffect, useRef, useContext } from "react";
import ReactMarkdown from "react-markdown";
import { useUsersContext } from "../context/usersContext";
import { usePromptsContaxt } from "../context/promptsContext";
import Icon from "./Icon";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import TurndownService from "turndown";
import MarkdownContent from "./MarkdownContent";

const turndownService = new TurndownService();

const Messages = () => {
  const {
    SSO,
    addNewMessage,
    users,
    chats,
    streamResponse,
    isStreaming,
    showToast,
    ChatSubmit,
    refresh,
    setUserAsUnread,
    userTyping,
    hideloader,
    toast,
    toastStyles,
    paramMessage
  } = useUsersContext();

  const [micStatus, setMicStatus] = useState("unknown");
  const [recordingLoading, setRecordingLoading] = useState(false);

  // Speech Recognition hooks
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const processedLen = useRef(0);
  const micStreamRef = useRef(null);

  const formatTime = (timeString) => {
    if (!timeString) return "";

    try {
      if (timeString.includes("PM") || timeString.includes("AM")) {
        const [time, period] = timeString.split(" ");
        const [hours, minutes] = time.split(":");
        return `${hours}:${minutes} ${period}`;
      }

      // If it's a timestamp, convert it
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  const { promptsData } = usePromptsContaxt();

  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // refs for individual message elements so we can measure their heights
  const messageRefs = useRef({});
  const [largeMessageIds, setLargeMessageIds] = useState(new Set());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, userTyping, streamResponse]);

  // Measure each message element and mark the ones whose height exceeds 200px.
  // useEffect(() => {
  //   const measureMessages = () => {
  //     try {
  //       const newSet = new Set();

  //       // determine which message refs are tall
  //       Object.keys(messageRefs.current).forEach((key) => {
  //         const el = messageRefs.current[key];
  //         if (el && el.clientHeight > 200) {
  //           newSet.add(key);
  //         }
  //       });

  //       // prune refs that no longer exist in messages to avoid growth over time
  //       const allowedKeys = new Set(messages.map((m, i) => String(m._id || i)));
  //       Object.keys(messageRefs.current).forEach((k) => {
  //         if (!allowedKeys.has(k)) delete messageRefs.current[k];
  //       });

  //       // update state (replace with a fresh Set to ensure re-render)
  //       setLargeMessageIds(newSet);
  //     } catch (e) {
  //       // ignore measurement errors
  //     }
  //   };

  //   // run measurement after render and on resize
  //   measureMessages();
  //   window.addEventListener("resize", measureMessages);
  //   return () => window.removeEventListener("resize", measureMessages);
  // }, [messages]);

  useEffect(() => {
    const measureMessages = () => {
      try {
        const newMap = new Map(); // Changed from Set to Map to store heights

        // determine which message refs are tall and store their heights
        Object.keys(messageRefs.current).forEach((key) => {
          const el = messageRefs.current[key];
          if (el && el.clientHeight > 200) {
            newMap.set(key, el.clientHeight);
          }
        });

        // prune refs that no longer exist in messages to avoid growth over time
        const allowedKeys = new Set(messages.map((m, i) => String(m._id || i)));
        Object.keys(messageRefs.current).forEach((k) => {
          if (!allowedKeys.has(k)) delete messageRefs.current[k];
        });

        // update state (replace with a fresh Map to ensure re-render)
        setLargeMessageIds(newMap);
      } catch (e) {
        // ignore measurement errors
      }
    };

    measureMessages();
    window.addEventListener("resize", measureMessages);
    return () => window.removeEventListener("resize", measureMessages);
    // ... rest of your useEffect code
  }, [messages]);

  // Update messages when users/chats data changes
  useEffect(() => {
    if (currentChatId && users.length > 0) {
      const currentChat = users.find((user) => user.id === currentChatId);
      if (currentChat && currentChat.messages) {
        const messageKey = Object.keys(currentChat.messages)[0];
        if (messageKey && currentChat.messages[messageKey]) {
          setMessages(currentChat.messages[messageKey]);
          // Turn off typing when messages update
          if (isTyping) {
            setIsTyping(false);
          }
        }
      }
    }
  }, [users, currentChatId]);

  // Monitor userTyping from context to show/hide typing indicator
  useEffect(() => {
    if (userTyping && currentChatId) {
      setIsTyping(true);
    } else if (!userTyping) {
      setIsTyping(false);
    }
  }, [userTyping, currentChatId]);

  // Monitor hideloader to stop typing animation
  useEffect(() => {
    if (hideloader === false) {
      setIsTyping(false);
    }
  }, [hideloader]);

  // Handle speech recognition transcript updates
  useEffect(() => {
    if (!listening) return;
    if (transcript.length > processedLen.current) {
      const diff = transcript.slice(processedLen.current); // only NEW words
      setNewMessage((prev) => prev + diff);
      processedLen.current = transcript.length; // move the pointer
    }
  }, [transcript, listening, setNewMessage]);

  // Debug: Log SSO data
  useEffect(() => {
    if (SSO) {
      console.log("SSO Data loaded:", SSO);
    }
  }, [SSO]);

  // Add message to chat - simplified for real API data
  const addMessage = (messageData) => {
    setMessages((prevMessages) => [...prevMessages, messageData]);
  };

  // Create new chat using context
  const createNewChat = async () => {
    try {
      const payload = {
        locationId: SSO?.id,
        username: SSO?.chat_username,
        assistant_id: SSO?.default_assistant || SSO?.assistant_id,
        assistant_name: "AI Assistant",
      };

      const newChat = await ChatSubmit(payload);

      if (!newChat?.response) {
        refresh(newChat);
        setCurrentChatId(newChat.id);
        setUserAsUnread(newChat.id);
        return newChat.id;
      } else {
        showToast("Failed to create new chat!");
        return null;
      }
    } catch (error) {
      console.error("Error creating new chat:", error);
      showToast("Error creating new chat!");
      return null;
    }
  };

  // Generate AI response using context
  const generateResponse = async (userMessage, chatId = null) => {
    try {
      const targetChatId = chatId || currentChatId;
      if (targetChatId && SSO) {
        // Use the context's addNewMessage function for real AI responses
        const assistant = {
          modal_name: "openai/gpt-4o",
          assistant_id: SSO?.default_assistant || SSO?.assistant_id,
          assistant_name: "AI Assistant",
        };

        // Add message using context function - this will handle the real API calls and typing states
        await addNewMessage(
          targetChatId,
          userMessage,
          userMessage,
          "savetodb",
          [], // selectedFiles
          assistant,
          "notimage"
        );

        // Let the context handle typing states completely
      } else {
        console.error("No chat ID or SSO available for sending message");
        showToast("Chat not ready. Please try again.");
      }
    } catch (error) {
      console.error("Error generating response:", error);
      showToast("Error sending message. Please try again.");
    }
  };

  // Auto-launch chat and send message if paramMessage is provided (once)
  const autoLaunchedRef = useRef(false);
  useEffect(() => {
    if (autoLaunchedRef.current) return;
    let pm = undefined;
    try {
      if (typeof paramMessage !== "undefined") {
        pm = String(paramMessage || "").trim();
      }
      if (!pm && typeof window !== "undefined") {
        const sp = new URLSearchParams(window.location.search);
        pm = (sp.get("paramMessage") || "").trim();
      }
    } catch (_) { }
    if (!pm) return;
    if (!SSO?.chat_username) return;
    autoLaunchedRef.current = true;
    (async () => {
      let chatId = currentChatId;
      if (!chatId) {
        chatId = await createNewChat();
        if (!chatId) return;
        setCurrentChatId(chatId);
      }
      await generateResponse(pm, chatId);
    })();
  }, [SSO, currentChatId]);

  // Handle sending message with context integration
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage = newMessage.trim();
    setNewMessage("");

    // If no current chat, create one first
    if (!currentChatId) {
      const newChatId = await createNewChat();
      if (newChatId) {
        setCurrentChatId(newChatId);
        // Send message immediately with the new chat ID
        await generateResponse(userMessage, newChatId);
      } else {
        showToast("Failed to create chat. Please try again.");
        setNewMessage(userMessage); // Restore the message
        return;
      }
    } else {
      // Generate AI response immediately if chat exists
      await generateResponse(userMessage);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startNewChat = async () => {
    setMessages([]);
    setNewMessage("");
    setIsTyping(false);
    setChatStarted(false);
    setCurrentChatId(null);
  };

  // Speech Recognition Functions
  const startListening = async () => {
    if (micStatus === "unknown") {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        micStreamRef.current = stream; // Save the stream reference
        setMicStatus("granted");
      } catch (err) {
        setMicStatus("denied");
        return;
      }
    }
    setRecordingLoading(true);
    SpeechRecognition.startListening({
      continuous: true,
      language: "en-US",
    });
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
    setRecordingLoading(false);
    resetTranscript();
    processedLen.current = 0;

    // Stop the original mic stream if present
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      setMicStatus("unknown");
    }
  };

  const handleVoiceSubmit = () => {
    if (!newMessage.trim()) return;
    handleSendMessage();
    resetTranscript();
    processedLen.current = 0;
  };

  const toggleMicrophone = () => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (value === "") {
      processedLen.current = 0;
      resetTranscript();
    }
  };

  // Check browser support
  if (!browserSupportsSpeechRecognition) {
    console.warn("Browser doesn't support speech recognition.");
  }

  const messageIcon = "https://snapshotstore.fra1.digitaloceanspaces.com/Maybe_Submark_black%20%281%29-77942";
  return (
    <div className="flex-grow h-full flex flex-col bg-white border border-[#ECDDFF] rounded-lg shadow-lg">
      <div className="flex items-center px-4 py-2 border border-b border-[#ECDDFF]">
        <img
          className="w-auto h-8 rounded-full mr-3"
          src={messageIcon}
          alt="avatar"
        />

        <div>
          <b>The Big AI Secret</b>
          <p>Powered by Maybe*</p>
        </div>
      </div>
      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="w-full flex-grow bg-gray-50 p-4 overflow-y-auto"
      >
        {messages.length === 0
          ? ""
          : messages.map((message, index) => (
            <div
              key={message._id || index}
              className={`flex ${message.sender === null ? "justify-end" : "items-end"
                } mb-4`}
            >

              <div className="max-w-[80%] lg:max-w-[80%]">
                <div
                  ref={(el) => {
                    const key = String(message._id || index);
                    if (el) messageRefs.current[key] = el;
                    else delete messageRefs.current[key];
                  }}
                  style={{
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    borderRadius: largeMessageIds.has(String(message._id || index)) ? '24px' : '30px'
                  }}
                  className={`px-6 py-4 flex items-start shadow-sm ${message.sender === null
                    ? "bg-[#F6F6F6] border border-[#ECDDFF] "
                    : "bg-[#F7F1FD] border border-[#ECDDFF] "
                    } h-fit w-fit`}
                >
                  {message.sender !== null && (
                    <img
                      className="w-8 h-8 rounded-full mr-3 flex-shrink-0"
                      src={messageIcon}
                      alt="avatar"
                    />
                  )}

                  <div
                    className={`flex-1 min-w-0 ${
                      message.sender === null ? "text-black" : "text-gray-800"
                    }`}
                  >
                    <div className="whitespace-pre-wrap break-words overflow-hidden">
                      {message.sender === null ? (
                        message.content
                      ) : (
                        <MarkdownContent content={message.content} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* Typing Indicator - Show only when context indicates typing and there's no streaming response */}
        {userTyping && currentChatId && (
          <div className="flex items-end mb-4">

            <div className="max-w-xs lg:max-w-md">
              <div className="bg-[#F7F1FD] border flex items-center border-[#ECDDFF] px-6 py-3 rounded-full shadow-sm">
                <img
                  className="w-auto h-8 rounded-full mr-3"
                  src={messageIcon}
                  alt="avatar"
                />

                <div className="">
                  Exploring research
                </div>

                <div className="flex space-x-1 ml-3">
                  <div className="w-2 h-2 bg-[#713FB1] rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-[#713FB1] rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-[#713FB1] rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>

              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className=" bg-white shadow-md rounded-b-lg">
        <div className="flex items-center space-x-3">
          <div className="flex border border-[#D9D9D9] rounded p-3 m-4 items-center flex-grow">
            <input
              value={newMessage}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              className="flex-grow px-4 py-3 text-gray-700 border-none outline-none"
              type="text"
              placeholder={listening ? "Listening..." : "Ask anything about the research..."}
              disabled={isTyping}
            />

            {/* Voice Recognition Button */}
            {/* <button
              onClick={() => {
                toggleMicrophone();
              }}
              style={{
                margin: "0 20px",
                cursor: "pointer",
                borderRadius: "50%",
                padding: "8px",
              }}
              title={listening ? "Stop recording" : "Start voice recording"}
            >
              <Icon id="microphone" />
            </button> */}

            {/* Loading/Stop Button - Show only when recording */}
            {/* {userTyping || recordingLoading ? (
              <button
                onClick={() => {
                  stopListening();
                }}
                title="Stop recording"
              >
                <Icon id="chatContinuous" />
              </button>
            ) : ( */}
            <button
              onClick={handleSendMessage}
              disabled={isTyping || !newMessage.trim()}
              className={(isTyping || !newMessage.trim() ? " sendChatBtn-disabled" : "sendChatBtn")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Icon id="sendChat" className="chat__input-icon" />
            </button>
            {/* )} */}
          </div>
        </div>
      </div>
      <div>{toast.isVisible && <p style={{ ...toastStyles, position: 'fixed', bottom: '50px', left: '50%', transform: 'translateX(-50%)', borderRadius: "4px", color: "white", padding: "8px 10px" }}>{toast.message}</p>}</div>
    </div>
  );
};

export default Messages;
